/**
 * Раздел «Оплаты» портала — этап P3 (§2, §3 ТЗ портала); с 21.10.2026 в
 * нём и прежняя «Сверка» (D267): поставщик написал «нет оплат по
 * накладным», а ответ лежал в двух разделах сразу.
 *
 * Пять вкладок: «Эта неделя» — платёжки с «Оплачено»; «Поставщики» — долг
 * в системе, по банку и по акту, из списка — карточка поставщика: приходы,
 * оплаты и акты одной лентой; «Выписка банка» — загрузка и раскладка
 * платежей по накладным; «Долги» — просрочено, поиск и карточка накладной;
 * «Отчёты» — `P2`–`P7`. Всё, что считается и разрешается, — на сервере:
 * оплаты — функциями сервиса `ops`, что и у бота, сверка — `reconcile-api`.
 * У каждой вкладки своя галочка (D235), и сервер отказывает без неё на
 * каждом запросе.
 */

import { Denied } from '@shell/states';
import type { Navigation } from '../../api';
import { Icon } from '../../icons';
import { canSee } from '../../nav';
import { navigate, useRoute } from '../../router';
import { ActScreen } from '../reconcile/ActView';
import { LedgerScreen } from '../reconcile/Ledger';
import { Statements } from '../reconcile/Statements';
import { Suppliers } from '../reconcile/Suppliers';
import '../reconcile/reconcile.css';
import { Debts } from './Debts';
import { InvoiceCard } from './InvoiceCard';
import { Reports } from './Reports';
import { Week } from './Week';
import { TAB_PATHS, paymentTabs, readPayments } from './paths';
import type { PaymentsTab } from './paths';
import './payments.css';

const TABS: { code: PaymentsTab; title: string }[] = [
  { code: 'week', title: 'Эта неделя' },
  { code: 'suppliers', title: 'Поставщики' },
  { code: 'statements', title: 'Выписка банка' },
  { code: 'debts', title: 'Долги' },
  { code: 'reports', title: 'Отчёты' },
];

export default function Payments({ nav }: { nav: Navigation }) {
  const route = useRoute();
  if (!canSee('payments', nav)) return <Denied />;
  const allowed = paymentTabs(nav);
  const read = readPayments(route.path);
  // Карточка накладной — общая для вкладок (право «Оплаты»); без открытых
  // вкладок раздел показывает только её. Карточка поставщика и акт — часть
  // «Поставщиков» и без их права не открываются.
  let tab: PaymentsTab | undefined;
  if (read.screen === 'tab') tab = allowed.includes(read.tab) ? read.tab : allowed[0];
  else tab = allowed.includes(read.tab) ? read.tab : undefined;
  if (!tab && read.screen !== 'invoice') return <Denied />;

  return (
    <div className="page payments-page">
      <header className="page__head payments-page__head">
        <h1 className="page__title">Оплаты</h1>
      </header>
      <div className="chips payments-tabs" role="tablist" aria-label="Оплаты">
        {TABS.filter((item) => allowed.includes(item.code)).map((item) => (
          <button
            key={item.code}
            type="button"
            role="tab"
            aria-selected={item.code === tab}
            className={`chip${item.code === tab ? ' chip--active' : ''}`}
            onClick={() => navigate(TAB_PATHS[item.code])}
          >
            {item.title}
          </button>
        ))}
      </div>
      {read.screen === 'invoice' ? (
        <>
          <button
            type="button"
            className="back-link"
            onClick={() => (window.history.length > 1 ? window.history.back() : navigate(TAB_PATHS.debts))}
          >
            <Icon name="back" size={16} /> Назад
          </button>
          <InvoiceCard id={read.invoiceId} />
        </>
      ) : read.screen === 'supplier' ? (
        <LedgerScreen id={read.supplierId} nav={nav} />
      ) : read.screen === 'act' ? (
        <ActScreen id={read.actId} nav={nav} />
      ) : tab === 'week' ? (
        <Week />
      ) : tab === 'suppliers' ? (
        <Suppliers />
      ) : tab === 'statements' ? (
        <Statements />
      ) : tab === 'debts' ? (
        <Debts />
      ) : (
        <Reports />
      )}
    </div>
  );
}
